/**
 * Reject widened string types (`string`) as `never` while allowing string
 * literal types (like `'one'`) and unions of literals (`'one' | 'two'`).
 *
 * Used to ensure that the type of an object's value is NOT `string` to help
 * ensure that `as const` was used on it.
 *
 * @example
 * T = 'one' // → string extends 'one' is false → result is 'one' (allowed)
 * T = 'one' | 'two' // → string extends ('one' | 'two') is false → result is 'one' | 'two' (allowed)
 * T = string // → string extends string is true → result is never (rejected)
 */
type RequireLiteralString<T> = string extends T ? never : T;

export type NonEmptyReadonlyArray<T> = readonly [T, ...T[]];

function assertNonEmpty<T>(
  array: readonly T[]
): asserts array is NonEmptyReadonlyArray<T> {
  if (array.length === 0) throw new Error('Expected at least one value');
}

/**
 * Produces a tuple consisting of the object's values, typed as a readonly array
 * of string literals.
 *
 * This is useful when defining `z.enum()` fields from existing "enum" objects.
 *
 * @example
 *
 * const Foo = {
 *   ONE: 'one',
 *   TWO: 'two',
 * } as const;
 *
 * const field = z.enum(getEnumFromObjectValues(Foo))
 *
 * field.parse('one')   ✅
 * field.parse('three') ❌ // ZodError
 *
 * const x: z.infer<typeof field> = 'one';   ✅
 * const y: z.infer<typeof field> = 'three'; ❌ // TypeScript error
 */
export function getEnumFromObjectValues<const T extends Record<string, string>>(
  enumLikeObject: T & {readonly [K in keyof T]: RequireLiteralString<T[K]>}
): NonEmptyReadonlyArray<T[keyof T]> {
  const values = Object.values(enumLikeObject);
  assertNonEmpty(values);
  return values;
}

/**
 * Produces a tuple consisting of the object's keys, typed as a readonly array
 * of string literals.
 *
 * This is useful when defining `z.enum()` fields from existing "enum" objects.
 *
 * @example
 *
 * const Foo = {
 *   ONE: 'one',
 *   TWO: 'two',
 * };
 *
 * const field = z.enum(getEnumFromObjectValues(Foo))
 *
 * field.parse('ONE')   ✅
 * field.parse('THREE') ❌ // ZodError
 *
 * const x: z.infer<typeof field> = 'ONE';   ✅
 * const y: z.infer<typeof field> = 'THREE'; ❌ // TypeScript error
 */
export function getEnumFromObjectKeys<T extends Record<string, unknown>>(
  enumLikeObject: T
): NonEmptyReadonlyArray<keyof T> {
  const values = Object.keys(enumLikeObject);
  assertNonEmpty(values);
  return values;
}
